import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import Layout, { LayoutEmbedContext } from '../../components/Layout';
import { can, HAB } from '../../variables/constants';
import { avecLibelleTraduit } from '../../i18n/libelles';

import Courriers from './Courriers';
import Bordereaux from './Bordereaux';
import RegistreJournalier from './RegistreJournalier';

/**
 * Page-hub du courrier.
 *
 * Trois écrans du même domaine occupaient trois entrées de menu : « Courriers »,
 * « Bordereaux coursier », « Registre journalier ». Un agent qui cherche une pièce
 * arrivée ce matin ne sait pas d'avance laquelle des trois la contient - il les
 * ouvre l'une après l'autre. Réunies en onglets, elles se parcourent sans repasser
 * par le menu.
 *
 * <h3>Les trois routes sont conservées</h3>
 * `/courriers`, `/courriers/bordereaux` et `/courriers/registre` mènent toutes ici,
 * et l'onglet actif se déduit du chemin. Conséquence : les liens existants, les
 * favoris et les entrées de menu du backend continuent de fonctionner à l'identique
 * - le regroupement du menu peut donc se faire APRÈS, sans rien casser entre-temps.
 * Naviguer entre onglets change l'URL : l'adresse reste partageable.
 *
 * Chaque écran est rendu dans `LayoutEmbedContext` : il s'enveloppe lui-même dans
 * `<Layout>`, qui devient transparent une fois embarqué. Aucun des trois n'a été
 * modifié.
 */

/**
 * Chaque onglet porte SON habilitation, et elles different reellement : le profil
 * COURSIER a BORDEREAU_READ sans avoir COURRIER_READ. Afficher les trois onglets a
 * tout le monde lui montrerait deux ecrans qui le renverraient en 403 - et surtout,
 * regrouper sans filtrer reviendrait a masquer le seul ecran qu'il utilise derriere
 * une rubrique a laquelle il n'a pas droit.
 */
const ONGLETS = avecLibelleTraduit([
  { key: 'courriers', path: '/courriers', hab: HAB.COURRIER_READ, Composant: Courriers },
  { key: 'bordereaux', path: '/courriers/bordereaux', hab: HAB.BORDEREAU_READ, Composant: Bordereaux },
  { key: 'registre', path: '/courriers/registre', hab: HAB.REGISTRE_READ, Composant: RegistreJournalier },
], 'courriers:onglets', { cle: 'key' });

/* Le chemin le plus specifique gagne : /courriers/bordereaux commence aussi par
   /courriers, tester dans l'ordre de declaration retournerait toujours le premier. */
const ongletDuChemin = (pathname) =>
  [...ONGLETS].sort((a, b) => b.path.length - a.path.length)
    .find((o) => pathname.startsWith(o.path))?.key ?? ONGLETS[0].key;

export default function CourrierLayout() {
  const { t } = useTranslation('courriers');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const visibles = ONGLETS.filter((o) => can(o.hab));
  const demande = ongletDuChemin(pathname);
  // Si le chemin vise un onglet auquel l'utilisateur n'a pas droit, on retombe sur
  // le premier qui lui est ouvert plutot que d'afficher un panneau vide.
  const actif = visibles.some((o) => o.key === demande) ? demande : visibles[0]?.key;

  if (!visibles.length) return null;

  return (
    <Layout title={t('titre')}>
      <Tabs
        activeKey={actif}
        onChange={(cle) => {
          const o = ONGLETS.find((x) => x.key === cle);
          if (o) navigate(o.path);
        }}
        items={visibles.map((o) => ({
          key: o.key,
          label: o.label,
          // Rendu a la demande : monter les trois ecrans d'emblee lancerait aussi
          // leurs trois chargements, dont deux que personne n'a demandes.
          children: actif === o.key ? (
            <LayoutEmbedContext.Provider value={true}>
              <o.Composant />
            </LayoutEmbedContext.Provider>
          ) : null,
        }))}
      />
    </Layout>
  );
}
